"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Shield,
  Users,
  Activity,
  Hash,
  AlertTriangle,
  Eye,
  Scale,
  X,
  Loader2,
} from "lucide-react";
import {
  PageWrapper,
  PageHeader,
  SectionCard,
  TabBar,
  StatCard,
  Button,
} from "@/components/ui";
import { formatDateTime, timeAgo } from "@/lib/utils";
import {
  type AuditLogEntry,
  staffMembers,
  type StaffMember,
  activeStaff,
  rolePermissions,
  type Permission,
  type StaffRole,
  type Dispute,
  hasPermission,
} from "@/lib/mock-data";
import Link from "next/link";
import { ConfirmDialog } from "@/components/confirm-dialog";

/* ── colour maps ─────────────────────────────── */

const actionColors: Record<string, string> = {
  trade_logged: "text-accent bg-accent/10",
  payment_added: "text-green-400 bg-green-500/10",
  payment_removed: "text-danger bg-danger/10",
  status_changed: "text-warning bg-warning/10",
  rate_updated: "text-blue-400 bg-blue-500/10",
  direction_changed: "text-purple-400 bg-purple-500/10",
  bank_txns_applied: "text-yellow-400 bg-yellow-500/10",
  treasury_tagged: "text-yellow-400 bg-yellow-500/10",
};

const channelColors: Record<string, string> = {
  dashboard: "text-accent bg-accent/10",
  whatsapp: "text-green-400 bg-green-500/10",
  telegram: "text-blue-400 bg-blue-500/10",
  api: "text-muted bg-surface-3",
};

const roleBadge: Record<StaffRole, string> = {
  owner: "text-accent bg-accent/10",
  admin: "text-blue-400 bg-blue-500/10",
  trader: "text-yellow-400 bg-yellow-500/10",
  viewer: "text-muted bg-surface-3",
};

const roleAvatar: Record<StaffRole, string> = {
  owner: "bg-accent/15 text-accent",
  admin: "bg-blue-500/15 text-blue-400",
  trader: "bg-yellow-500/15 text-yellow-400",
  viewer: "bg-surface-3 text-muted",
};

const allPermissions: Permission[] = [
  "log_trade",
  "edit_trade",
  "add_payment",
  "remove_payment",
  "select_from_bank",
  "tag_treasury",
  "change_direction",
  "change_rate",
  "dispute",
  "view_audit_log",
  "manage_staff",
];

const allRoles: StaffRole[] = ["owner", "admin", "trader", "viewer"];

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatPermission(p: string) {
  return p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const statusBadgeClass: Record<string, string> = {
  open: "text-danger bg-danger/15",
  under_review: "text-warning bg-warning/15",
  resolved: "text-success bg-success/15",
  dismissed: "text-muted bg-surface-3",
};

const priorityBadgeClass: Record<string, string> = {
  critical: "text-danger bg-danger/10",
  high: "text-warning bg-warning/10",
  medium: "text-accent bg-accent/10",
  low: "text-muted bg-surface-3",
};

/* ── Activity Log Tab ─────────────────────────────── */

function ActivityLogTab() {
  const [search, setSearch] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);

  useEffect(() => {
    fetch("/api/audit-log")
      .then((r) => r.ok ? r.json() : [])
      .then((entries: AuditLogEntry[]) => setAuditLog(entries))
      .catch(() => {});
  }, []);

  const uniqueStaff = useMemo(
    () => [...new Set(auditLog.map((e) => e.staffName))],
    [auditLog],
  );
  const uniqueActions = useMemo(
    () => [...new Set(auditLog.map((e) => e.action))],
    [auditLog],
  );
  const uniqueChannels = useMemo(
    () => [...new Set(auditLog.map((e) => e.channel))],
    [auditLog],
  );

  const filtered = useMemo(() => {
    return [...auditLog]
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
      .filter((e) => {
        if (staffFilter !== "all" && e.staffName !== staffFilter) return false;
        if (actionFilter !== "all" && e.action !== actionFilter) return false;
        if (channelFilter !== "all" && e.channel !== channelFilter)
          return false;
        if (search) {
          const q = search.toLowerCase();
          return (
            e.details.toLowerCase().includes(q) ||
            e.staffName.toLowerCase().includes(q) ||
            e.action.toLowerCase().includes(q) ||
            (e.tradeId && `#${e.tradeId}`.includes(q))
          );
        }
        return true;
      });
  }, [search, staffFilter, actionFilter, channelFilter, auditLog]);

  const selectClass =
    "bg-surface-2 text-foreground text-[12px] border border-border rounded-md px-2 py-1.5 outline-none focus:border-accent/50 transition-colors cursor-pointer";

  return (
    <div className="mt-4 space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
          <input
            type="text"
            placeholder="Search activity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-surface-2 text-foreground text-[12px] border border-border rounded-md pl-8 pr-3 py-1.5 outline-none focus:border-accent/50 transition-colors placeholder:text-muted/60"
          />
        </div>
        <select
          value={staffFilter}
          onChange={(e) => setStaffFilter(e.target.value)}
          className={selectClass}
        >
          <option value="all">All Staff</option>
          {uniqueStaff.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className={selectClass}
        >
          <option value="all">All Actions</option>
          {uniqueActions.map((a) => (
            <option key={a} value={a}>
              {formatPermission(a)}
            </option>
          ))}
        </select>
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className={selectClass}
        >
          <option value="all">All Channels</option>
          {uniqueChannels.map((c) => (
            <option key={c} value={c}>
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <SectionCard>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-muted uppercase tracking-wider text-[10px]">
                <th className="px-4 py-2.5 font-semibold">Timestamp</th>
                <th className="px-4 py-2.5 font-semibold">Staff</th>
                <th className="px-4 py-2.5 font-semibold">Action</th>
                <th className="px-4 py-2.5 font-semibold">Details</th>
                <th className="px-4 py-2.5 font-semibold">Trade</th>
                <th className="px-4 py-2.5 font-semibold">Channel</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-t border-border/50 hover:bg-surface-2/50 transition-colors"
                >
                  <td className="px-4 py-2.5 text-muted whitespace-nowrap tabular-nums">
                    {formatDateTime(entry.timestamp)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ${
                          roleAvatar[
                            staffMembers.find((s) => s.id === entry.staffId)
                              ?.role ?? "viewer"
                          ]
                        }`}
                      >
                        {initials(entry.staffName)}
                      </div>
                      <span className="text-foreground">
                        {entry.staffName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        actionColors[entry.action] ?? "text-muted bg-surface-3"
                      }`}
                    >
                      {formatPermission(entry.action)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-muted max-w-[280px] truncate">
                    {entry.details}
                  </td>
                  <td className="px-4 py-2.5">
                    {entry.tradeId ? (
                      <Link
                        href="/ledger"
                        className="text-accent hover:underline font-medium"
                      >
                        #{entry.tradeId}
                      </Link>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        channelColors[entry.channel] ??
                        "text-muted bg-surface-3"
                      }`}
                    >
                      {entry.channel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm font-medium text-foreground mb-1">
                No activity found
              </p>
              <p className="text-[13px] text-muted">
                Try adjusting your filters or search query.
              </p>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

/* ── Staff Card ─────────────────────────────── */

function StaffCard({ member }: { member: StaffMember }) {
  const [expanded, setExpanded] = useState(false);
  const permissions = rolePermissions[member.role];
  const isYou = member.id === activeStaff.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className="bg-surface rounded-lg p-4"
      style={{ border: "1px solid var(--border)" }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${roleAvatar[member.role]}`}
        >
          {initials(member.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-medium text-foreground truncate">
              {member.name}
            </p>
            {isYou && (
              <span className="text-[9px] font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                You
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted truncate">{member.email}</p>
        </div>
      </div>

      {/* Meta */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${roleBadge[member.role]}`}
        >
          {member.role}
        </span>
        <span className="flex items-center gap-1 text-[11px]">
          <span
            className={`w-1.5 h-1.5 rounded-full ${member.isActive ? "bg-accent" : "bg-muted/40"}`}
          />
          <span className={member.isActive ? "text-accent" : "text-muted"}>
            {member.isActive ? "Active" : "Inactive"}
          </span>
        </span>
        <span className="text-[10px] text-muted ml-auto">
          {timeAgo(member.lastActive)}
        </span>
      </div>

      {/* Expand permissions */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-[11px] text-muted hover:text-foreground transition-colors cursor-pointer w-full"
      >
        {expanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
        {permissions.length} permission{permissions.length !== 1 && "s"}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 flex flex-wrap gap-1">
              {permissions.map((p) => (
                <span
                  key={p}
                  className="text-[10px] text-muted bg-surface-2 rounded px-1.5 py-0.5"
                >
                  {formatPermission(p)}
                </span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Staff Tab ─────────────────────────────── */

function StaffTab() {
  return (
    <div className="mt-4 space-y-6">
      {/* Staff Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {staffMembers.map((member) => (
          <StaffCard key={member.id} member={member} />
        ))}
      </div>

      {/* Permission Matrix */}
      <SectionCard title="Role Permission Matrix">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-muted uppercase tracking-wider text-[10px]">
                <th className="px-4 py-2.5 font-semibold">Permission</th>
                {allRoles.map((role) => (
                  <th
                    key={role}
                    className="px-4 py-2.5 font-semibold text-center"
                  >
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded ${roleBadge[role]}`}
                    >
                      {role}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allPermissions.map((perm) => (
                <tr
                  key={perm}
                  className="border-t border-border/50 hover:bg-surface-2/50 transition-colors"
                >
                  <td className="px-4 py-2 text-foreground">
                    {formatPermission(perm)}
                  </td>
                  {allRoles.map((role) => (
                    <td key={role} className="px-4 py-2 text-center">
                      {rolePermissions[role].includes(perm) ? (
                        <CheckCircle2 className="w-4 h-4 text-accent inline-block" />
                      ) : (
                        <span className="text-muted/30">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

/* ── Disputes Tab ─────────────────────────────── */

function DisputesTab() {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ total: number; open: number; underReview: number; resolved: number; dismissed: number; byPriority: Record<string, number>; avgResolutionTimeSecs: number } | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [cpSearch, setCpSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<"dismiss" | "under_review" | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [inlineResolve, setInlineResolve] = useState<string | null>(null);
  const [resolveType, setResolveType] = useState<"refund" | "partial_refund" | "credit_to_balance" | "dismissed">("dismissed");
  const [resolveNote, setResolveNote] = useState("");
  const [submittingResolve, setSubmittingResolve] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch("/api/disputes").then((r) => r.json()),
      fetch("/api/disputes/stats").then((r) => r.json()),
    ])
      .then(([dData, sData]) => {
        setDisputes(dData.disputes || []);
        setStats(sData.stats || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    return disputes
      .filter((d) => {
        if (statusFilter !== "all" && d.status !== statusFilter) return false;
        if (priorityFilter !== "all" && d.priority !== priorityFilter) return false;
        if (cpSearch) {
          const q = cpSearch.toLowerCase();
          return d.counterpartyName.toLowerCase().includes(q) || d.id.toLowerCase().includes(q);
        }
        return true;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [disputes, statusFilter, priorityFilter, cpSearch]);

  const allSelected = filtered.length > 0 && filtered.every((d) => selectedIds.has(d.id));
  const canManage = hasPermission(activeStaff, "dispute");

  const handleBulkAction = useCallback(async (action: "dismiss" | "under_review") => {
    setBulkProcessing(true);
    const ids = [...selectedIds];
    for (const id of ids) {
      const d = disputes.find((x) => x.id === id);
      if (!d) continue;
      // Only apply to open/under_review disputes
      if (action === "dismiss" && (d.status !== "open" && d.status !== "under_review")) continue;
      if (action === "under_review" && d.status !== "open") continue;

      try {
        await fetch(`/api/trades/${d.tradeId}/disputes`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            disputeId: id,
            status: action === "dismiss" ? "dismissed" : "under_review",
            staffRole: activeStaff.role,
            authorName: activeStaff.name,
            ...(action === "dismiss" ? {
              resolution: { type: "dismissed", note: "Bulk dismissed", resolvedBy: activeStaff.name, resolvedAt: new Date().toISOString() },
            } : {}),
          }),
        });
      } catch { /* continue */ }
    }
    setSelectedIds(new Set());
    setBulkAction(null);
    setBulkProcessing(false);
    fetchData();
  }, [selectedIds, disputes, activeStaff, fetchData]);

  const handleInlineResolve = useCallback(async (disputeId: string) => {
    if (!resolveNote.trim()) return;
    setSubmittingResolve(true);
    const d = disputes.find((x) => x.id === disputeId);
    if (!d) { setSubmittingResolve(false); return; }
    try {
      await fetch(`/api/trades/${d.tradeId}/disputes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          disputeId,
          status: resolveType === "dismissed" ? "dismissed" : "resolved",
          resolution: {
            type: resolveType,
            note: resolveNote,
            resolvedBy: activeStaff.name,
            resolvedAt: new Date().toISOString(),
          },
          staffRole: activeStaff.role,
          authorName: activeStaff.name,
        }),
      });
      setInlineResolve(null);
      setResolveNote("");
      setResolveType("dismissed");
      fetchData();
    } catch { /* */ }
    setSubmittingResolve(false);
  }, [disputes, resolveType, resolveNote, activeStaff, fetchData]);

  const selectClass = "bg-surface-2 text-foreground text-[12px] border border-border rounded-md px-2 py-1.5 outline-none focus:border-accent/50 transition-colors cursor-pointer";

  const formatDuration = (secs: number) => {
    if (secs < 3600) return `${Math.round(secs / 60)}m`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h`;
    return `${Math.round(secs / 86400)}d`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 text-muted animate-spin" />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Open" value={String(stats.open + stats.underReview)} subValue={`${stats.open} open, ${stats.underReview} reviewing`} delay={0} />
          <StatCard label="Critical" value={String(stats.byPriority?.critical ?? 0)} subValue={`${stats.byPriority?.high ?? 0} high priority`} delay={0.05} />
          <StatCard label="Avg Resolution" value={stats.avgResolutionTimeSecs > 0 ? formatDuration(stats.avgResolutionTimeSecs) : "—"} subValue={`${stats.resolved} resolved total`} delay={0.1} />
          <StatCard label="Resolution Rate" value={stats.total > 0 ? `${Math.round(((stats.resolved + stats.dismissed) / stats.total) * 100)}%` : "—"} subValue={`${stats.resolved + stats.dismissed} of ${stats.total} total`} delay={0.15} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
          <input
            type="text"
            placeholder="Search counterparty or ID..."
            value={cpSearch}
            onChange={(e) => setCpSearch(e.target.value)}
            className="w-full bg-surface-2 text-foreground text-[12px] border border-border rounded-md pl-8 pr-3 py-1.5 outline-none focus:border-accent/50 transition-colors placeholder:text-muted/60"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="under_review">Under Review</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className={selectClass}>
          <option value="all">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && canManage && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-2" style={{ border: "1px solid var(--border)" }}>
          <span className="text-[12px] text-foreground font-medium">{selectedIds.size} selected</span>
          <button
            onClick={() => setBulkAction("dismiss")}
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-danger bg-danger/10 hover:bg-danger/20 transition-colors"
          >
            Dismiss Selected ({selectedIds.size})
          </button>
          <button
            onClick={() => setBulkAction("under_review")}
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-warning bg-warning/10 hover:bg-warning/20 transition-colors"
          >
            Mark Under Review ({selectedIds.size})
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-[11px] text-muted hover:text-foreground transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <SectionCard>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-muted uppercase tracking-wider text-[10px]">
                {canManage && (
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => {
                        if (allSelected) setSelectedIds(new Set());
                        else setSelectedIds(new Set(filtered.map((d) => d.id)));
                      }}
                      className="accent-accent"
                    />
                  </th>
                )}
                <th className="px-3 py-2.5 font-semibold">ID</th>
                <th className="px-3 py-2.5 font-semibold">Trade</th>
                <th className="px-3 py-2.5 font-semibold">Counterparty</th>
                <th className="px-3 py-2.5 font-semibold">Reason</th>
                <th className="px-3 py-2.5 font-semibold">Priority</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Filed</th>
                {canManage && <th className="px-3 py-2.5 font-semibold">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} className="border-t border-border/50 hover:bg-surface-2/50 transition-colors">
                  {canManage && (
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(d.id)}
                        onChange={() => {
                          const next = new Set(selectedIds);
                          if (next.has(d.id)) next.delete(d.id);
                          else next.add(d.id);
                          setSelectedIds(next);
                        }}
                        className="accent-accent"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-muted font-mono text-[10px]">{d.id.slice(0, 12)}...</td>
                  <td className="px-3 py-2.5">
                    <Link href="/ledger" className="text-accent hover:underline font-medium">#{d.tradeId}</Link>
                  </td>
                  <td className="px-3 py-2.5 text-foreground">{d.counterpartyName}</td>
                  <td className="px-3 py-2.5 text-muted max-w-[160px] truncate">{d.reason}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${priorityBadgeClass[d.priority] || "text-muted bg-surface-3"}`}>
                      {d.priority}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${statusBadgeClass[d.status] || "text-muted bg-surface-3"}`}>
                      {d.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-muted whitespace-nowrap">{timeAgo(d.createdAt)}</td>
                  {canManage && (
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {d.status === "open" && (
                          <button
                            onClick={async () => {
                              await fetch(`/api/trades/${d.tradeId}/disputes`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ disputeId: d.id, status: "under_review", staffRole: activeStaff.role, authorName: activeStaff.name }),
                              });
                              fetchData();
                            }}
                            className="px-2 py-1 rounded text-[10px] font-medium text-warning bg-warning/10 hover:bg-warning/20 transition-colors"
                            title="Mark Under Review"
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                        )}
                        {(d.status === "open" || d.status === "under_review") && (
                          <button
                            onClick={() => { setInlineResolve(d.id); setResolveType("dismissed"); setResolveNote(""); }}
                            className="btn-inline-action px-2 py-1 rounded text-[10px] font-medium text-accent transition-all cursor-pointer"
                            title="Resolve"
                          >
                            <Scale className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {inlineResolve === d.id && (
                        <div className="mt-2 p-2 rounded-lg bg-surface space-y-2" style={{ border: "1px solid var(--border)" }}>
                          <select
                            value={resolveType}
                            onChange={(e) => setResolveType(e.target.value as typeof resolveType)}
                            className="w-full bg-surface-2 text-[11px] text-foreground border border-border rounded px-2 py-1 outline-none"
                          >
                            <option value="dismissed">Dismiss</option>
                            <option value="refund">Refund</option>
                            <option value="partial_refund">Partial Refund</option>
                            <option value="credit_to_balance">Credit to Balance</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Resolution note..."
                            value={resolveNote}
                            onChange={(e) => setResolveNote(e.target.value)}
                            className="w-full bg-surface-2 text-[11px] text-foreground border border-border rounded px-2 py-1 outline-none placeholder:text-muted/50"
                          />
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleInlineResolve(d.id)}
                              disabled={!resolveNote.trim() || submittingResolve}
                              className="btn-inline-action flex-1 px-2 py-1 rounded text-[10px] font-medium text-accent disabled:opacity-40 transition-all cursor-pointer"
                            >
                              {submittingResolve ? "..." : "Confirm"}
                            </button>
                            <button
                              onClick={() => setInlineResolve(null)}
                              className="px-2 py-1 rounded text-[10px] text-muted hover:text-foreground transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="w-6 h-6 text-muted mb-2" />
              <p className="text-sm font-medium text-foreground mb-1">No disputes found</p>
              <p className="text-[13px] text-muted">Try adjusting your filters.</p>
            </div>
          )}
        </div>
      </SectionCard>

      {/* Bulk action confirm */}
      <ConfirmDialog
        open={!!bulkAction}
        title={bulkAction === "dismiss" ? `Dismiss ${selectedIds.size} disputes?` : `Mark ${selectedIds.size} disputes under review?`}
        message={bulkAction ? `This will ${bulkAction === "dismiss" ? "dismiss" : "mark as under review"} all selected disputes.` : ""}
        confirmLabel={bulkProcessing ? "Processing..." : "Confirm"}
        onConfirm={() => { if (bulkAction) handleBulkAction(bulkAction); }}
        onCancel={() => setBulkAction(null)}
        variant={bulkAction === "dismiss" ? "danger" : "warning"}
      />
    </div>
  );
}

/* ── Admin Page ─────────────────────────────── */

export default function AdminPage() {
  const [tab, setTab] = useState("Activity Log");
  const [adminAuditLog, setAdminAuditLog] = useState<AuditLogEntry[]>([]);

  useEffect(() => {
    fetch("/api/audit-log")
      .then((r) => r.ok ? r.json() : [])
      .then((entries: AuditLogEntry[]) => setAdminAuditLog(entries))
      .catch(() => {});
  }, []);

  const uniqueActions = new Set(adminAuditLog.map((e) => e.action));
  const activeCount = staffMembers.filter((s) => s.isActive).length;
  const roleCount = new Set(staffMembers.map((s) => s.role)).size;

  return (
    <PageWrapper>
      <PageHeader
        title="Admin"
        subtitle="Activity log and staff management"
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard
          label="Total Actions"
          value={String(adminAuditLog.length)}
          subValue={`${uniqueActions.size} action types`}
          delay={0}
        />
        <StatCard
          label="Active Staff"
          value={String(activeCount)}
          subValue={`of ${staffMembers.length} members`}
          delay={0.05}
        />
        <StatCard
          label="Staff Members"
          value={String(staffMembers.length)}
          subValue={`${staffMembers.filter((s) => !s.isActive).length} inactive`}
          delay={0.1}
        />
        <StatCard
          label="Roles"
          value={String(roleCount)}
          subValue="owner, admin, trader, viewer"
          delay={0.15}
        />
      </div>

      {/* Tabs */}
      <TabBar
        tabs={["Activity Log", "Staff", "Disputes"]}
        active={tab}
        onChange={setTab}
      />

      {tab === "Activity Log" && <ActivityLogTab />}
      {tab === "Staff" && <StaffTab />}
      {tab === "Disputes" && <DisputesTab />}
    </PageWrapper>
  );
}
